import { Box, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import IconCross from 'assets/img/icons/cross.svg?react';
import { ContextMenuButton, ContextMenuItem, LwIconButton } from 'redesign';
import {
  IconCalendar,
  IconClock,
  IconDuplicate,
  IconEdit,
  IconFlexPools,
  IconLocation,
  IconProfileUsers,
  IconTrash,
} from 'assets/img';
import { formatDateFromString, formatFloatNumber } from 'shared/utils/formatting';
import { AuthenticatedElement } from 'ogp/components/common/authenticated-element';
import { PolicyGroups } from 'shared/utils/policy-controller';
import { CompanyRole } from '@types';
import { ProjectDetail } from 'api/services/project/project-service.schema';
import { CertificatesDetail } from '../../common/certificates-detail';
import { FlexpoolsDetail } from '../../common/flexpools-detail';
import { HourlyRates } from '../../common/hourly-rates';

type ProjectDetailHeadProps = {
  data: ProjectDetail;
  companyName: string;
  close: () => void;
  handleEdit: () => void;
  handleDuplicate: () => void;
  handleArchive: () => void;
  showTempWorkRate: boolean;
};

export const ProjectDetailHead = ({
  data,
  companyName,
  close,
  handleEdit,
  handleDuplicate,
  handleArchive,
  showTempWorkRate,
}: ProjectDetailHeadProps) => {
  const ContextMenu = () => {
    return (
      <Box>
        <ContextMenuButton size="medium" color="secondary">
          <ContextMenuItem onClick={handleEdit}>
            <IconEdit />
            Wijzig
          </ContextMenuItem>
          <ContextMenuItem onClick={handleDuplicate}>
            <IconDuplicate />
            Dupliceer
          </ContextMenuItem>
          <AuthenticatedElement policyGroup={PolicyGroups.COMPANY} roles={CompanyRole.HQADMIN}>
            <ContextMenuItem onClick={handleArchive}>
              <IconTrash />
              Verwijderen
            </ContextMenuItem>
          </AuthenticatedElement>
        </ContextMenuButton>
      </Box>
    );
  };

  return (
    <Box>
      <Box display="flex" flexDirection="row">
        <StyledCompanyInfo>
          <Typography noWrap color="secondary" variant="body1" style={{ fontWeight: 'bold' }}>
            {companyName} / {data.department.branchOffice.name}, {data.department.name}
          </Typography>
          <StyledJobName noWrap variant="h5">
            {data.name}
          </StyledJobName>
        </StyledCompanyInfo>
        <Box
          display="flex"
          flexDirection="row"
          ml="auto"
          alignItems="middle"
          style={{ gap: '32px' }}
        >
          <ContextMenu />
          <LwIconButton
            iconColorMode="stroke"
            onClick={close}
            color="secondary"
            data-testid="close-button"
          >
            <IconCross width={24} height={24} />
          </LwIconButton>
        </Box>
      </Box>
      <StyledPropertiesBox>
        <StyledInfoBox>
          <CenterTextBox>
            <StyledIcon as={IconProfileUsers} width={24} height={24} />
            <Typography noWrap variant="subtitle2">
              {data.placements.filter((placement) => placement.state === 'active').length} /{' '}
              {data.maxPlacements}
            </Typography>
          </CenterTextBox>
          <CenterTextBox>
            <HourlyRates
              freelanceWage={data.freelanceProperties?.hourlyRateCents}
              tempworkWage={data.tempWorkProperties?.grossHourlyRateCents}
              showTempWorkRate={showTempWorkRate}
            />
          </CenterTextBox>
        </StyledInfoBox>
        <StyledInfoBox>
          <CenterTextBox>
            <StyledIcon as={IconCalendar} width={24} height={24} />
            <Typography noWrap variant="subtitle2">
              {formatDateFromString(data.startDate)} - {formatDateFromString(data.endDate)}
            </Typography>
          </CenterTextBox>
          <CenterTextBox>
            <StyledIcon as={IconClock} width={24} height={24} />
            <Typography noWrap variant="subtitle2">
              {formatFloatNumber(data.weekHourVolume)} uren per week
            </Typography>
          </CenterTextBox>
        </StyledInfoBox>
        <StyledInfoBox>
          <CenterTextBox>
            <StyledIcon as={IconCalendar} width={24} height={24} />
            <CertificatesDetail certificates={data.certificates} />
          </CenterTextBox>
          <CenterTextBox>
            <StyledIcon as={IconFlexPools} width={24} height={24} />
            <Typography noWrap variant="subtitle2">
              <FlexpoolsDetail flexPools={data.flexPools} />
            </Typography>
          </CenterTextBox>
          <CenterTextBox>
            <StyledIcon as={IconLocation} width={24} height={24} />
            <Tooltip title={data.location.address}>
              <Typography noWrap variant="subtitle2">
                {data.location.street} {data.location.houseNumber}
              </Typography>
            </Tooltip>
          </CenterTextBox>
        </StyledInfoBox>
      </StyledPropertiesBox>
    </Box>
  );
};

const StyledPropertiesBox = styled(Box)(({ theme }) => ({
  gap: theme.spacing(3),
  '& svg': {
    opacity: 0.6,
  },
  display: 'flex',
  flexDirection: 'row',
  marginTop: theme.spacing(5),
}));

const StyledInfoBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
}));

const StyledIcon = styled('svg')(({ theme }) => ({
  marginRight: theme.spacing(3),
  flexShrink: 0,
}));

const CenterTextBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  marginBottom: theme.spacing(3),
}));

const StyledJobName = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const StyledCompanyInfo = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
}));
